import * as types from './actionTypes';
import { getRequest, putRequest, postRequest, deleteRequest } from '../../lib/apiUtils';
import { RecipientTypes } from '../../lib/systemVariables';
import { clearRecipientSummaryCache } from '../../lib/recipientSummaryCache';

export function getOrganizations() {
    return async dispatch => {
        dispatch({ type: types.START_GET_ORGANIZATIONS });

        const response = await getRequest("/recipients", { recipientTypes: [RecipientTypes.Organization] });
        response.data = response.data.records
            ? response.data.records.map(recipient => ({ key: recipient.applicantId, value: recipient.lastOrOrgName, tags: recipient.tags, address: recipient.address })) : [];

        dispatch({ type: types.GET_ORGANIZATIONS, response });
    }
}

export function searchRecipients(search, page = 1, results = 12) {
    return async (dispatch, getState) => {
        let filterSettings = getState().recipientAccounts.filters;
        filterSettings.elasticSearchText = search;
        if(search.length > 0) {
            const response = await getRequest('/search/applicants', {text: search, page, results});
            filterSettings.recipients = response.data || {};
        }
        else {
            filterSettings.recipients = {};
        }
        dispatch({ type: types.UPDATE_RECIPIENTS_FILTERS, update: filterSettings });
    }
}

export function filterRecipients(update = null, search = "", results = 12, sort = { by: 'lastOrOrgName', direction: 'Ascending' }) {
    return dispatch => {
        dispatch({ type: update ? types.UPDATE_RECIPIENTS_FILTERS : types.RESET_RECIPIENTS_FILTERS, update });

        if (!update) {
            dispatch({ type: types.CLEAR_RECIPIENTS_TABLE_SELECTION });
        }

        dispatch(getRecipientAccounts(search, 1, results, sort));
    }
}

export function updateTable(settings) {
    return dispatch => {
        dispatch({ type: types.UPDATE_RECIPIENTS_TABLE, settings });
    }
}

export function toggleSelectRecipient(recipient) {
    return dispatch => {
        dispatch({ type: types.TOGGLE_SELECT_RECIPIENTS_TABLE_RECORD, ...{ record: recipient, recordIdKey: 'applicantId' } });
    }
}

export function clearRecipientsSelection() {
    return dispatch => {
        dispatch({ type: types.CLEAR_RECIPIENTS_TABLE_SELECTION });
    }
}

export function getRecipientAccounts(search = "", page = 1, results = 12, sort) {
    return async (dispatch, getState) => {
        const filterSettings = getState().recipientAccounts.filters;
        filterSettings.elasticSearchText = search;
        if(search.length > 0 && !sort) {
            sort = { by: 'recipientIds', direction: 'Ascending' };
        }

        const settings = { page, results, sort: sort || { by: 'name', direction: 'Ascending' } };
        dispatch({ type: types.UPDATE_RECIPIENTS_TABLE, settings });
        const tableSettings = getState().recipientsTable;

        const requestId = ++getState().recipientsTable.currentRequestId;
        dispatch({ type: types.START_GET_RECIPIENTS_TABLE_RECORDS, requestId });

        let params = {
            page: tableSettings.page,
            sortBy: tableSettings.sort.by,
            sortDirection: tableSettings.sort.direction,
            results: tableSettings.resultsView
        };

        for (var filterProperty in filterSettings) {
            let filterVal = filterSettings[filterProperty];
            params[filterProperty] = filterVal;
        }

        const response = await getRequest('/recipients', params);

        dispatch({ type: types.GET_RECIPIENTS_TABLE_RECORDS, ...{ response, requestId } });
    };
}

export function deleteRecipientAccount(recipientId) {
    return async dispatch => {
        dispatch({ type: types.START_REMOVE_RECIPIENTS_TABLE_RECORD });

        const response = await deleteRequest(`/recipients/${recipientId}`);
        response.recordIdKey = 'applicantId';
        response.recordId = recipientId;

        dispatch({ type: types.REMOVE_RECIPIENTS_TABLE_RECORD, response });
    };
}

export function getRecipientAccountDetails(applicantId, preserveDetails ) {
    return async dispatch => {
        dispatch({ type: types.START_GET_RECIPIENT_DETAILS, preserveDetails });
        const response = await getRequest(`/recipients/${applicantId}`);
        dispatch({ type: types.GET_RECIPIENT_DETAILS, response });
    };
}

export function getRecipientApplicationsForExport(fields, recipientId, startDate, endDate) {
    return async (dispatch) => {

        let params = {
            fields, 
            startDate,
            endDate
        };

        const response = await getRequest(`/recipients/${recipientId}/applications/report`, params);

        dispatch({ type: types.GET_RECIPIENT_APPLICATIONS_FOR_EXPORT, ...{ response } });
    };
}

export function getUpdatedRecipientAccountDetails(applicantId) {
    return async dispatch => {
        const response = await getRequest(`/recipients/${applicantId}`);
        dispatch({ type: types.GET_RECIPIENT_DETAILS, response });
    }
}

export function getRecipientApplicationHistory(applicantId) {
    return async dispatch => {
        dispatch({ type: types.START_GET_RECIPIENT_APPLICATION_HISTORY });

        const response = await getRequest(`/recipients/${applicantId}/applications`, null, []);
        response.data = response.data instanceof Array ? response.data.map(app => ({ ...app, applicantId })) : [];

        dispatch({ type: types.GET_RECIPIENT_APPLICATION_HISTORY, response });
    };
}

export function updateRecipientDetails(recipientId, recipient) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_RECIPIENT_DETAILS });

        const response = await putRequest(`/recipients/${recipientId}`, recipient, {});
        if (response.success) clearRecipientSummaryCache(recipientId);

        dispatch({ type: types.UPDATE_RECIPIENT_DETAILS, response });
    }
}

async function getNote(recipientId, noteId) {
    return await getRequest(`/recipients/${recipientId}/notes/${noteId}`);
}

export function addRecipientNote(recipientId, noteText) {
    return async dispatch => {
        dispatch({ type: types.START_ADD_RECIPIENT_NOTE });

        const response = await postRequest(`/recipients/${recipientId}/notes`, { noteText });
        if (response.success) clearRecipientSummaryCache(recipientId);

        if(response.success) {
            const note = await getNote(recipientId, response.data);
            response.data = note.data;
        }

        dispatch({ type: types.ADD_RECIPIENT_NOTE, response });
    };
}

export function updateRecipientNote(recipientId, noteId, noteText) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_RECIPIENT_NOTE });

        const response = await putRequest(`/recipients/${recipientId}/notes/${noteId}`, { noteText });
        if (response.success) clearRecipientSummaryCache(recipientId);

        const note = await getNote(recipientId, noteId);
        response.data = note.data;

        dispatch({ type: types.UPDATE_RECIPIENT_NOTE, response });
    };
}

export function deleteRecipientNote(recipientId, noteId) {
    return async dispatch => {
        dispatch({ type: types.START_DELETE_RECIPIENT_NOTE });

        const response = await deleteRequest(`/recipients/${recipientId}/notes/${noteId}`);
        if (response.success) clearRecipientSummaryCache(recipientId);

        dispatch({ type: types.DELETE_RECIPIENT_NOTE, ...{ response, noteId } });
    };
}

export function createRecipient(recipient) {
    const recipientObj = {
        applicantType: recipient.applicantType,
        isIsraeli: recipient.isIsraeli,
        personTitleId: recipient.personTitleId,
        firstName: recipient.firstName,
        lastName: recipient.lastName,
        reportName: recipient.reportName,
        organization: recipient.organization,
        address: recipient.address,
        previousAddress: recipient.previousAddress,
        phoneNumber: recipient.phoneNumber,
        phoneType: recipient.phoneType,
        secondPhoneNumber: recipient.secondPhoneNumber,
        secondPhoneType: recipient.secondPhoneType,
        email: recipient.email,
        teudatZahut: recipient.teudatZehut,
        bankRoutingNumber: recipient.bankRoutingNumber,
        bankBranchNumber: recipient.bankBranchNumber,
        bankAccountNumber: recipient.bankAccountNumber,
        bankAccountHolder: recipient.bankAccountHolder,
        tags: recipient.tags,
        note: recipient.note,
        trustedPayeeId: recipient.trustedPayeeId,
        references: recipient.references,
        solicitors: recipient.solicitors,
        roshYeshivas: recipient.roshYeshivas,
        applicantPledges: recipient.applicantPledges,
    };

    return async dispatch => {

        dispatch({ type: types.START_CREATE_RECIPIENT });
        const response = await postRequest('/recipients', recipientObj, {});
        dispatch({ type: types.CREATE_RECIPIENT, response });
    }
}

export function getRecipientsForExport(fields, groupBy) {
    return async (dispatch, getState) => {
        const filterSettings = getState().recipientAccounts.filters;

        let params = {
            fields,
            groupBy
        };

        for (var filterProperty in filterSettings) {
            let filterVal = filterSettings[filterProperty];
            params[filterProperty] = filterVal;
        }

        const response = await getRequest('/recipients/report', params);

        dispatch({ type: types.GET_RECIPIENTS_FOR_EXPORT, ...{ response } });
    };
}

export function getPledgesReport(fundIds = [], includeClosedPledges = false) {
    return async dispatch => {
        dispatch({ type: types.START_GET_PLEDGES_REPORT });
        const response = await getRequest('/recipients/report/pledge', {
            fundIds,
            includeClosedPledges
        });

        if (response.success && typeof response.data === 'string') {
            try {
                response.data = JSON.parse(response.data);
            } catch (error) {
            }
        }

        dispatch({ type: types.GET_PLEDGES_REPORT, response });
        return response;
    };
}





// WEBPACK FOOTER //
// ./src/redux/actions/RecipientAccountsActions.js